import type { Address } from '@medplum/fhirtypes'

interface StateTimeZones {
  [state: string]: string
}

// US state / territory (USPS 2-letter) → IANA timezone. Coarse but correct for
// scheduling: one representative zone per state. States that legitimately span
// zones are mapped to their most-populous zone; refine with a lat/long lookup
// only if a multi-zone state's minority region becomes a real scheduling need.
const STATE_TIME_ZONE: StateTimeZones = {
  AL: 'America/Chicago',
  AK: 'America/Anchorage',
  AZ: 'America/Phoenix', // no DST
  AR: 'America/Chicago',
  CA: 'America/Los_Angeles',
  CO: 'America/Denver',
  CT: 'America/New_York',
  DE: 'America/New_York',
  DC: 'America/New_York',
  FL: 'America/New_York',
  GA: 'America/New_York',
  HI: 'Pacific/Honolulu', // no DST
  ID: 'America/Boise',
  IL: 'America/Chicago',
  IN: 'America/Indiana/Indianapolis',
  IA: 'America/Chicago',
  KS: 'America/Chicago',
  KY: 'America/New_York',
  LA: 'America/Chicago',
  ME: 'America/New_York',
  MD: 'America/New_York',
  MA: 'America/New_York',
  MI: 'America/Detroit',
  MN: 'America/Chicago',
  MS: 'America/Chicago',
  MO: 'America/Chicago',
  MT: 'America/Denver',
  NE: 'America/Chicago',
  NV: 'America/Los_Angeles',
  NH: 'America/New_York',
  NJ: 'America/New_York',
  NM: 'America/Denver',
  NY: 'America/New_York',
  NC: 'America/New_York',
  ND: 'America/Chicago',
  OH: 'America/New_York',
  OK: 'America/Chicago',
  OR: 'America/Los_Angeles',
  PA: 'America/New_York',
  RI: 'America/New_York',
  SC: 'America/New_York',
  SD: 'America/Chicago',
  TN: 'America/Chicago',
  TX: 'America/Chicago',
  UT: 'America/Denver',
  VT: 'America/New_York',
  VA: 'America/New_York',
  WA: 'America/Los_Angeles',
  WV: 'America/New_York',
  WI: 'America/Chicago',
  WY: 'America/Denver',
  PR: 'America/Puerto_Rico',
}

// Zone to reason in when no address resolves one. Clinic-local reasoning must
// never silently fall back to UTC: weekday and day boundaries shift.
export const DEFAULT_CLINIC_TIME_ZONE = 'America/Los_Angeles'

// Resolves an IANA timezone for a FHIR Address from its state. Returns
// undefined when the address has no state or the state is unrecognized.
export function timezoneForAddress(
  address: Address | undefined
): string | undefined {
  const state = address?.state?.trim().toUpperCase()
  if (state && STATE_TIME_ZONE[state]) {
    return STATE_TIME_ZONE[state]
  }
  return undefined
}
